import { FC, useCallback, useMemo, useRef, useState } from 'react';
import cn from 'classnames';
import { Button, cssX, Dropdown, useOutsideClick } from '@datassential/platform-ui-lib';
import { IconPpt, IconXls } from '@icons';
import ConfirmationPopup from '@components/ConfirmationPopup/ConfirmationPopup';
import useTopTrendsExport from '@core/apiHooks/useTopTrendsExport';
import { useTopTrends } from './TopTrendsContext';

const DROPDOWN_ID = 'top-trends-export-button-dropdown';

const FooterContent: FC = () => {
    const { exportRequest, tableData, onReset } = useTopTrends();
    const hasData = tableData.length > 0;
    const { isExporting, downloadXls, downloadPpt } = useTopTrendsExport();
    const [isDownloadMenuOpened, setIsDownloadMenuOpened] = useState(false);
    const [isClearPopupOpened, setIsClearPopupOpened] = useState(false);
    const downloadButtonRef = useRef<HTMLDivElement>(null);

    useOutsideClick({
        ref: downloadButtonRef,
        callback: () => setIsDownloadMenuOpened(false),
        ignoreNodesIds: [DROPDOWN_ID],
    });

    const handleExportXls = useCallback(() => {
        if (!hasData || isExporting) return;
        downloadXls(exportRequest);
        setIsDownloadMenuOpened(false);
    }, [hasData, isExporting, exportRequest, downloadXls]);

    const handleExportPpt = useCallback(() => {
        if (!hasData || isExporting) return;
        downloadPpt(exportRequest);
        setIsDownloadMenuOpened(false);
    }, [hasData, isExporting, exportRequest, downloadPpt]);

    const exportMenuOptions = useMemo(
        () => [
            {
                id: 'xls',
                title: 'Top Trends',
                onClick: handleExportXls,
                icon: <IconXls className={cssX('Dropdown__icon')} />,
            },
            {
                id: 'ppt',
                title: 'Top & Trending',
                onClick: handleExportPpt,
                icon: <IconPpt className={cssX('Dropdown__icon')} />,
            },
        ],
        [handleExportXls, handleExportPpt]
    );

    const handleReset = useCallback(() => {
        onReset();
        setIsClearPopupOpened(false);
    }, [onReset]);

    // @ts-ignore
    return (
        <>
            <Button
                onClick={() => setIsClearPopupOpened(true)}
                className="page-footer__btn is-secondary"
            >
                Reset
            </Button>
            <div ref={downloadButtonRef}>
                <Button
                    data-tooltip-id={DROPDOWN_ID}
                    onClick={() => setIsDownloadMenuOpened(!isDownloadMenuOpened)}
                    isDisabled={!hasData || isExporting}
                >
                    Download
                </Button>
            </div>
            <Dropdown
                id={DROPDOWN_ID}
                className={cssX('ExportDropdown')}
                isOpened={isDownloadMenuOpened}
                place="top-start"
                columns={[{
                    id: 'id',
                    items: [],
                    content: (
                        <>
                            {exportMenuOptions.map(option => (
                                <div
                                    key={option.id}
                                    className={cn(cssX('Dropdown__row'))}
                                >
                                    <div
                                        className={cn(
                                            cssX('Dropdown__item'),
                                            cssX('SelectComponent__item'),
                                        )}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                        }}
                                        onClick={option.onClick}
                                    >
                                        {option.icon}
                                        &nbsp;&nbsp;
                                        {option.title}
                                    </div>
                                </div>
                            ))}
                        </>
                    ),
                }]}
            />
            <ConfirmationPopup
                closeModal={() => setIsClearPopupOpened(false)}
                shown={isClearPopupOpened}
                title="Clear keywords and filters"
                // @ts-ignore this is a JSX component so ignore TS error due to lacking types for it
                text="Are you sure you want to clear all keywords and filters?"
                onConfirm={handleReset}
            />
        </>
    );
};

export default FooterContent;
